/**
 * Environment Detection Utility
 * Single source of truth for environment detection across the application
 *
 * @usage
 * ```typescript
 * import { isProduction, isDevelopment } from '@/lib/environment';
 *
 * if (isProduction()) {
 *   // Production-only behavior
 * }
 *
 * if (isDevelopment()) {
 *   // Development-only behavior
 * }
 * ```
 */

/**
 * Check if running in production mode
 * Uses Vite's built-in PROD flag as the primary source of truth
 *
 * Note: AWS Amplify automatically sets the correct environment variables
 * (including Clerk keys) based on deployment environment.
 */
export function isProduction(): boolean {
  return import.meta.env.PROD;
}

/**
 * Check if running in development mode
 * Uses Vite's built-in DEV flag as the primary source of truth
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Get environment-specific configuration
 * Helper for conditional configuration based on environment
 *
 * @example
 * const apiUrl = getEnvConfig(
 *   'http://localhost:3000',  // development
 *   'https://api.infinityvaultcards.com'  // production
 * );
 */
export function getEnvConfig<T>(devConfig: T, prodConfig: T): T {
  return isDevelopment() ? devConfig : prodConfig;
}
